/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {FC, useState, useEffect} from 'react'
import {useIntl} from 'react-intl'
import {KTIcon} from '../../../helpers'
import {getUpgradePrice, upgrade} from './_requests'
import Swal from 'sweetalert2'
import {useAuth} from '../../../../app/modules/auth'

type Prices = {
  [key: string]: {
    monthly: string
    annual: string
    weekly?: string
    quarter?: string
    half_year?: string
    analysis_limit?: string
  }
}

/** Language-agnostic plan codes (backend and DB); display names come from i18n */
const PLAN_CODES = ['basic', 'pro', 'premium'] as const
type PlanApiKey = (typeof PLAN_CODES)[number]

/** Billing cycles supported by backend */
const BILLING_CYCLES = ['week', 'month', 'quarter', 'half_year', 'annual'] as const
type BillingCycle = (typeof BILLING_CYCLES)[number]

const PLAN_TITLE_KEYS: Record<PlanApiKey, string> = {
  basic: 'UPGRADE.PLAN_BASIC',
  pro: 'UPGRADE.PLAN_PRO',
  premium: 'UPGRADE.PLAN_PREMIUM',
}
const PLAN_SUB_KEYS: Record<PlanApiKey, string> = {
  basic: 'UPGRADE.PLAN_BASIC_SUB',
  pro: 'UPGRADE.PLAN_PRO_SUB',
  premium: 'UPGRADE.PLAN_PREMIUM_SUB',
}

const PERIOD_LABEL_KEYS: Record<BillingCycle, string> = {
  week: 'UPGRADE.PERIOD_WEEK',
  month: 'UPGRADE.PERIOD_MONTH',
  quarter: 'UPGRADE.PERIOD_QUARTER',
  half_year: 'UPGRADE.PERIOD_HALF_YEAR',
  annual: 'UPGRADE.PERIOD_ANNUAL',
}

const UNIT_LABEL_KEYS: Record<BillingCycle, string> = {
  week: 'UPGRADE.UNIT_PER_WEEK',
  month: 'UPGRADE.UNIT_PER_MONTH',
  quarter: 'UPGRADE.UNIT_PER_QUARTER',
  half_year: 'UPGRADE.UNIT_PER_HALF_YEAR',
  annual: 'UPGRADE.UNIT_PER_ANNUAL',
}

const UPGRADE_ERROR_CODE_KEYS: Record<string, string> = {
  validation_failed: 'UPGRADE.ERROR_VALIDATION',
  invalid_plan_or_billing: 'UPGRADE.ERROR_INVALID_PLAN',
  plan_already_active: 'UPGRADE.CURRENT_PLAN_DESC',
  insufficient_wallet: 'UPGRADE.ERROR_INSUFFICIENT_WALLET',
  upgrade_failed: 'UPGRADE.ERROR_UPGRADE_FAILED',
}

function getUpgradeErrorMessageKey(code?: string, status?: number): string {
  if (code && UPGRADE_ERROR_CODE_KEYS[code]) {
    return UPGRADE_ERROR_CODE_KEYS[code]
  }
  if (status === 422) return 'UPGRADE.ERROR_VALIDATION'
  if (status === 400) return 'UPGRADE.ERROR_INVALID_PLAN'
  if (status === 409) return 'UPGRADE.CURRENT_PLAN_DESC'
  if (status === 403) return 'UPGRADE.ERROR_INSUFFICIENT_WALLET'
  if (status === 500) return 'UPGRADE.ERROR_UPGRADE_FAILED'
  return 'UPGRADE.ERROR_TEXT'
}

type PlanFeature = {titleKey: string; titleParams?: Record<string, string | number>; supported: boolean}

type PlanItem = {
  apiKey: PlanApiKey
  priceMonth: string
  priceAnnual: string
  priceWeek?: string
  priceQuarter?: string
  priceHalfYear?: string
  default: boolean
  custom: boolean
  labelKey: string | null
  features: PlanFeature[]
}

function getPriceForCycle(plan: PlanItem, cycle: BillingCycle): string {
  const monthly = parseInt(plan.priceMonth, 10) || 0
  const annual = parseInt(plan.priceAnnual, 10) || 0
  if (cycle === 'week') return plan.priceWeek ?? String(Math.round(monthly / 4))
  if (cycle === 'month') return plan.priceMonth || '0'
  if (cycle === 'quarter') return plan.priceQuarter ?? String(monthly * 3)
  if (cycle === 'half_year') return plan.priceHalfYear ?? String(monthly * 6)
  return plan.priceAnnual || String(annual)
}

function buildAnalysisFeature(limit: number): PlanFeature {
  if (limit === -1) {
    return {titleKey: 'UPGRADE.FEATURE_ANALYSES_UNLIMITED', supported: true}
  }
  if (limit > 0) {
    return {titleKey: 'UPGRADE.FEATURE_ANALYSES_LIMIT', titleParams: {count: limit}, supported: true}
  }
  return {titleKey: 'UPGRADE.FEATURE_ANALYSES_NONE', supported: false}
}

const UpgradePlan: FC<{onClose: () => void; onRequestAddCredit?: () => void}> = ({
  onClose,
  onRequestAddCredit,
}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const [plans, setPlans] = useState<PlanItem[]>([
    {
      apiKey: 'basic',
      priceMonth: '',
      priceAnnual: '',
      default: true,
      custom: false,
      labelKey: null,
      features: [
        {titleKey: 'UPGRADE.FEATURE_ANALYSES_NONE', supported: false},
        {titleKey: 'UPGRADE.FEATURE_SUPPORT', supported: false},
      ],
    },
    {
      apiKey: 'pro',
      priceMonth: '',
      priceAnnual: '',
      default: false,
      custom: false,
      labelKey: null,
      features: [
        {titleKey: 'UPGRADE.FEATURE_ANALYSES_NONE', supported: false},
        {titleKey: 'UPGRADE.FEATURE_SUPPORT', supported: true},
      ],
    },
    {
      apiKey: 'premium',
      priceMonth: '',
      priceAnnual: '',
      default: false,
      custom: false,
      labelKey: 'UPGRADE.PLAN_LABEL_POPULAR',
      features: [
        {titleKey: 'UPGRADE.FEATURE_ANALYSES_NONE', supported: false},
        {titleKey: 'UPGRADE.FEATURE_SUPPORT', supported: true},
      ],
    },
  ])

  const [currentState, setCurrentState] = useState<BillingCycle>('month')
  const [selected, setSelected] = useState<PlanApiKey>('basic')

  const isCurrentPlanActive = (planKey: PlanApiKey, cycle: BillingCycle) => {
    if (!currentUser?.plan_expiry) return false
    const expiry = new Date(currentUser.plan_expiry)
    if (Number.isNaN(expiry.getTime()) || expiry <= new Date()) return false
    return currentUser.plan === planKey && currentUser.billing_cycle === cycle
  }

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const prices = (await getUpgradePrice()).data as Prices
        setPlans((prevPlans) =>
          prevPlans.map((plan) => {
            const planData = prices[plan.apiKey]
            if (planData) {
              const limit = parseInt(planData.analysis_limit ?? '0', 10) || 0
              return {
                ...plan,
                priceMonth: planData.monthly || plan.priceMonth,
                priceAnnual: planData.annual || plan.priceAnnual,
                priceWeek: planData.weekly ?? plan.priceWeek,
                priceQuarter: planData.quarter ?? plan.priceQuarter,
                priceHalfYear: planData.half_year ?? plan.priceHalfYear,
                features: [
                  buildAnalysisFeature(limit),
                  {titleKey: 'UPGRADE.FEATURE_SUPPORT', supported: plan.apiKey !== 'basic'},
                ],
              }
            }
            return plan
          })
        )
      } catch (error) {
        console.error('Error fetching prices:', error)
      }
    }

    fetchPrices()
  }, [])

  const handleUpgrade = async () => {
    if (isCurrentPlanActive(selected, currentState)) {
      Swal.fire({
        icon: 'info',
        title: intl.formatMessage({id: 'UPGRADE.CURRENT_PLAN_TITLE'}),
        text: intl.formatMessage({id: 'UPGRADE.CURRENT_PLAN_DESC'}),
        confirmButtonText: intl.formatMessage({id: 'UPGRADE.CONFIRM_BUTTON'}),
      })
      return
    }

    try {
      await upgrade(selected, currentState)
      Swal.fire({
        icon: 'success',
        title: intl.formatMessage({id: 'UPGRADE.SUCCESS_TITLE'}),
        text: intl.formatMessage({id: 'UPGRADE.SUCCESS_TEXT'}),
        confirmButtonText: intl.formatMessage({id: 'UPGRADE.CONFIRM_BUTTON'}),
      })

      onClose()
    } catch (error: any) {
      const status = error.response?.status as number | undefined
      const code = error.response?.data?.code as string | undefined
      const messageKey = getUpgradeErrorMessageKey(code, status)
      const message = intl.formatMessage({id: messageKey})
      const isWalletError = code === 'insufficient_wallet' || status === 403

      if (isWalletError) {
        Swal.fire({
          icon: 'warning',
          title: intl.formatMessage({id: 'UPGRADE.ERROR_TITLE'}),
          text: message,
          showCancelButton: true,
          confirmButtonText: intl.formatMessage({id: 'UPGRADE.ADD_CREDIT_BUTTON'}),
          cancelButtonText: intl.formatMessage({id: 'UPGRADE.CANCEL'}),
        }).then((result) => {
          if (result.isConfirmed && onRequestAddCredit) {
            onClose()
            onRequestAddCredit()
          }
        })
        return
      }

      Swal.fire({
        icon: 'error',
        title: intl.formatMessage({id: 'UPGRADE.ERROR_TITLE'}),
        text: message,
        confirmButtonText: intl.formatMessage({id: 'UPGRADE.CONFIRM_BUTTON'}),
      })
    }
  }

  return (
    <div className='modal fade show d-block' tabIndex={-1} aria-modal='true'>
      <div className='modal-dialog modal-xl'>
        <div className='modal-content rounded'>
          <div className='modal-header justify-content-end border-0 pb-0'>
            <button onClick={onClose} className='btn btn-sm btn-icon btn-active-color-primary'>
              <KTIcon iconName='cross' className='fs-1' />
            </button>
          </div>

          <div className='modal-body pt-0 pb-15 px-5 px-xl-20'>
            <div className='mb-13 text-center'>
              <h1 className='mb-3'>{intl.formatMessage({id: 'UPGRADE.MODAL_TITLE'})}</h1>
              <div className='text-muted fw-bold fs-5'>
                {intl.formatMessage({id: 'UPGRADE.MODAL_CONTACT'})}
              </div>
            </div>

            <div className='d-flex flex-column'>
              <div className='nav-group nav-group-outline mx-auto flex-wrap justify-content-center' data-kt-buttons='true'>
                {BILLING_CYCLES.map((cycle) => (
                  <a
                    key={cycle}
                    href='#'
                    className={
                      'btn btn-color-gray-400 btn-active btn-active-secondary px-4 py-2 me-2 mb-2 ' +
                      (currentState === cycle ? 'active' : '')
                    }
                    onClick={(e) => {
                      e.preventDefault()
                      setCurrentState(cycle)
                    }}
                  >
                    {intl.formatMessage({id: PERIOD_LABEL_KEYS[cycle]})}
                  </a>
                ))}
              </div>

              <div className='row mt-10'>
                <div className='col-lg-6 mb-10 mb-lg-0'>
                  <div className='nav flex-column'>
                    {plans.map((plan, index) => {
                      const isCurrent = isCurrentPlanActive(plan.apiKey, currentState)
                      return (
                        <div
                          onClick={() => {
                            if (!isCurrent) setSelected(plan.apiKey)
                          }}
                          className={
                            `nav-link btn btn-outline btn-outline-dashed btn-color-dark d-flex flex-stack text-start p-6 ` +
                            (index !== plans.length - 1 && 'mb-6 ') +
                            (plan.default && 'active ') +
                            (!plan.custom && 'btn-active btn-active-primary ') +
                            (isCurrent ? ' opacity-75 border-success bg-light-success' : '')
                          }
                          data-bs-toggle='tab'
                          data-bs-target={`#kt_upgrade_plan_${index}`}
                          key={plan.apiKey}
                        >
                          <div className='d-flex align-items-center me-2'>
                            <div className='form-check form-check-custom form-check-solid form-check-success me-6'>
                              <input
                                className='form-check-input'
                                type='radio'
                                name='plan'
                                value={plan.apiKey}
                                checked={selected === plan.apiKey}
                                disabled={isCurrent}
                                onChange={(e) => setSelected(e.target.value as PlanApiKey)}
                              />
                            </div>
                            <div className='flex-grow-1'>
                              <h2 className='d-flex align-items-center fs-2 fw-bolder flex-wrap'>
                                {intl.formatMessage({id: PLAN_TITLE_KEYS[plan.apiKey]})}
                                {isCurrent && (
                                  <span className='badge badge-light-success me-2 fs-7'>
                                    {intl.formatMessage({id: 'UPGRADE.CURRENT_PLAN_BADGE'})}
                                  </span>
                                )}
                                {plan.labelKey && (
                                  <span className='badge badge-light-success me-2 fs-7'>
                                    {intl.formatMessage({id: plan.labelKey})}
                                  </span>
                                )}
                              </h2>
                              <div className='fw-bold opacity-50'>
                                {intl.formatMessage({id: PLAN_SUB_KEYS[plan.apiKey]})}
                              </div>
                            </div>
                          </div>

                          <div className='ms-5'>
                            {!plan.custom && (
                              <>
                                <span data-kt-element='period'>
                                  {intl.formatMessage({id: UNIT_LABEL_KEYS[currentState]})}
                                </span>
                                /
                                <span className='fs-3x fw-bolder'>
                                  {getPriceForCycle(plan, currentState)}
                                </span>
                                <span className='fs-7 opacity-50'>
                                  <span className='mb-2'>
                                    {intl.formatMessage({id: 'UPGRADE.UNIT_CREDIT'})}
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>

                <div className='col-lg-6'>
                  <div className='tab-content rounded h-100 bg-light p-10'>
                    {plans
                      .filter((plan) => plan.apiKey === selected)
                      .map((plan, index) => (
                        <div key={`custom-${plan.apiKey}`}>
                          {!plan.custom && (
                            <div
                              className={`tab-pane fade` + (plan.default && 'show active')}
                              id={`kt_upgrade_plan_${index}`}
                            >
                              <div className='pt-1'>
                                {plan.features.map((feature, i) => (
                                  <div
                                    className={
                                      `d-flex align-items-center` +
                                      (i !== plan.features.length - 1 ? ' mb-7' : '')
                                    }
                                    key={`${i}-${feature.titleKey}`}
                                  >
                                    {feature.supported ? (
                                      <>
                                        <span className='fw-bold fs-5 text-gray-700 flex-grow-1'>
                                          {intl.formatMessage(
                                            {id: feature.titleKey},
                                            feature.titleParams as Record<string, string> | undefined
                                          )}
                                        </span>
                                        <KTIcon
                                          iconName='check-circle'
                                          className='fs-1 text-success'
                                        />
                                      </>
                                    ) : (
                                      <>
                                        <span className='fw-bold fs-5 text-gray-400 flex-grow-1'>
                                          {intl.formatMessage(
                                            {id: feature.titleKey},
                                            feature.titleParams as Record<string, string> | undefined
                                          )}
                                        </span>
                                        <KTIcon iconName='cross-circle' className='fs-1' />
                                      </>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>

            <div className='d-flex flex-center flex-row-fluid pt-12'>
              <button
                type='reset'
                onClick={onClose}
                className='btn btn-light ms-3'
                data-bs-dismiss='modal'
              >
                {intl.formatMessage({id: 'UPGRADE.CANCEL'})}
              </button>

              <button
                type='submit'
                className='btn btn-primary'
                onClick={handleUpgrade}
                disabled={isCurrentPlanActive(selected, currentState)}
              >
                {intl.formatMessage({id: 'UPGRADE.SUBMIT'})}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export {UpgradePlan}
